import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from './ui/use-toast';
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Loader2,
  Calendar,
  Activity,
  Plus,
  ExternalLink,
  Clock,
  BarChart3
} from 'lucide-react';
import { API_URL } from '../config/api';
import { authFetch, handleResponse } from '../utils/auth-fetch';

interface IntegrationData {
  matomo?: {
    exists: boolean;
    siteId?: string;
    name?: string;
    hasData?: boolean;
    isRecent?: boolean;
    lastDataTime?: string;
    visitsToday?: number;
    visitsYesterday?: number;
    totalVisits?: number;
    error?: string;
  };
  openreplay?: {
    exists: boolean;
    projectId?: string;
    projectKey?: string;
    name?: string;
    hasData?: boolean;
    isRecent?: boolean;
    lastDataTime?: string;
    sessionsToday?: number;
    sessionsYesterday?: number;
    totalSessions?: number;
    avgSessionDuration?: number;
    error?: string;
  };
  snowplow?: {
    exists: boolean;
    trackerId?: string;
    appId?: string;
    name?: string;
    hasData?: boolean;
    isRecent?: boolean;
    lastDataTime?: string;
    eventsToday?: number;
    eventsYesterday?: number;
    totalEvents?: number;
    uniqueVisitorsToday?: number;
    error?: string;
  };
}

interface IntegrationStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  site: {
    id?: string;
    _id?: string; // Keep for backward compatibility
    url: string;
    name: string;
  };
}

export default function IntegrationStatusModal({ isOpen, onClose, site }: IntegrationStatusModalProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<{ [key: string]: boolean }>({});
  const [creating, setCreating] = useState<{ [key: string]: boolean }>({});
  const [integrationData, setIntegrationData] = useState<IntegrationData>({});

  // Check integration status for all services
  const checkAllIntegrations = async () => {
    setLoading(true);
    
    try {
      // Check integration status using the unified endpoint
      const response = await authFetch('/integration-status/check', {
        method: 'POST',
        body: JSON.stringify({ siteId: site.id || site._id })
      });

      const data = await handleResponse<IntegrationData>(response);
      const newData: IntegrationData = data;

      setIntegrationData(newData);
    } catch (error) {
      console.error('Error checking integrations:', error);
      toast({
        title: 'Error',
        description: 'Failed to check integration status',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Refresh single integration
  const refreshIntegration = async (service: string) => {
    setRefreshing({ ...refreshing, [service]: true });
    
    try {
      // Just refresh all integrations for simplicity
      await checkAllIntegrations();
    } catch (error) {
      toast({
        title: 'Error',
        description: `Failed to refresh ${service} status`,
        variant: 'destructive'
      });
    } finally {
      setRefreshing({ ...refreshing, [service]: false });
    }
  };

  // Fix integration (search or create)
  const fixIntegration = async (service: string) => {
    setCreating({ ...creating, [service]: true });
    
    try {
      const response = await authFetch(`/integration-status/fix-${service}`, {
        method: 'POST',
        body: JSON.stringify({ siteId: site.id || site._id })
      });
      
      const result = await handleResponse(response);
      
      toast({
        title: 'Success',
        description: result.message || `${service} integration fixed successfully`,
      });
      
      // Refresh integration status
      await checkAllIntegrations();
    } catch (error) {
      console.error(`Error fixing ${service} integration:`, error);
      toast({
        title: 'Error',
        description: `Failed to fix ${service} integration`,
        variant: 'destructive'
      });
    } finally {
      setCreating({ ...creating, [service]: false });
    }
  };

  useEffect(() => {
    if (isOpen) {
      checkAllIntegrations();
    }
  }, [isOpen]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getStatusIcon = (data: any) => {
    if (data?.error) return <AlertCircle className="h-5 w-5 text-destructive" />;
    if (!data?.exists) return <XCircle className="h-5 w-5 text-destructive" />;
    if (data?.isRecent) return <CheckCircle className="h-5 w-5 text-green-600" />;
    if (data?.hasData) return <AlertCircle className="h-5 w-5 text-yellow-600" />;
    return <XCircle className="h-5 w-5 text-muted-foreground" />;
  };

  const getStatusBadge = (data: any) => {
    if (data?.error) return <Badge variant="destructive">Error</Badge>;
    if (!data?.exists) return <Badge variant="destructive">Not Found</Badge>;
    if (data?.isRecent) return <Badge variant="default">Active</Badge>;
    if (data?.hasData) return <Badge variant="secondary">Has Data</Badge>;
    return <Badge variant="outline">No Data</Badge>;
  };

  const renderIntegrationCard = (
    service: string,
    title: string,
    data: any,
    identifierLabel: string,
    identifierValue?: string
  ) => (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            {getStatusIcon(data)}
            {title}
          </CardTitle>
          <div className="flex items-center gap-2">
            {getStatusBadge(data)}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => refreshIntegration(service)}
              disabled={refreshing[service]}
            >
              {refreshing[service] ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {data?.error && !data?.exists ? (
          <div className="space-y-3">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{data.error}</AlertDescription>
            </Alert>
            <Button 
              onClick={() => fixIntegration(service)}
              disabled={creating[service]}
              className="w-full"
              size="sm"
            >
              {creating[service] ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Fixing...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Fix {title} Integration
                </>
              )}
            </Button>
          </div>
        ) : data?.error ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{data.error}</AlertDescription>
          </Alert>
        ) : data?.exists ? (
          <>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{identifierLabel}:</span>
                <span className="font-mono">{identifierValue || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Name:</span>
                <span>{data.name || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Last Data:</span>
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {data.lastDataTime ? formatDate(data.lastDataTime) : 'Never'}
                </span>
              </div>
            </div>
            
            {data.hasData && (
              <div className="border-t pt-3 space-y-2">
                <h4 className="text-sm font-medium flex items-center gap-1">
                  <BarChart3 className="h-4 w-4" />
                  Activity Summary
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {service === 'matomo' && (
                    <>
                      <div>
                        <span className="text-muted-foreground">Today:</span>
                        <span className="ml-1 font-medium">{data.visitsToday || 0} visits</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Yesterday:</span>
                        <span className="ml-1 font-medium">{data.visitsYesterday || 0} visits</span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-muted-foreground">Total:</span>
                        <span className="ml-1 font-medium">{data.totalVisits || 0} visits</span>
                      </div>
                    </>
                  )}
                  {service === 'openreplay' && (
                    <>
                      <div>
                        <span className="text-muted-foreground">Today:</span>
                        <span className="ml-1 font-medium">{data.sessionsToday || 0} sessions</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Yesterday:</span>
                        <span className="ml-1 font-medium">{data.sessionsYesterday || 0} sessions</span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-muted-foreground">Avg Duration:</span>
                        <span className="ml-1 font-medium">{data.avgSessionDuration || 0}s</span>
                      </div>
                    </>
                  )}
                  {service === 'snowplow' && (
                    <>
                      <div>
                        <span className="text-muted-foreground">Today:</span>
                        <span className="ml-1 font-medium">{data.eventsToday || 0} events</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Yesterday:</span>
                        <span className="ml-1 font-medium">{data.eventsYesterday || 0} events</span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-muted-foreground">Unique Visitors:</span>
                        <span className="ml-1 font-medium">{data.uniqueVisitorsToday || 0} today</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-3">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                This site is not configured in {title}.
              </AlertDescription>
            </Alert>
            <Button 
              onClick={() => fixIntegration(service)}
              disabled={creating[service]}
              className="w-full"
              size="sm"
            >
              {creating[service] ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Fixing...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Fix {title} Integration
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Integration Status</DialogTitle>
          <DialogDescription>
            Check and manage {site.name} integration with analytics platforms
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="matomo">Matomo</TabsTrigger>
              <TabsTrigger value="openreplay">OpenReplay</TabsTrigger>
              <TabsTrigger value="snowplow">Snowplow</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4">
              <div className="grid gap-4">
                {renderIntegrationCard(
                  'matomo',
                  'Matomo Analytics',
                  integrationData.matomo,
                  'Site ID',
                  integrationData.matomo?.siteId
                )}
                {renderIntegrationCard(
                  'openreplay',
                  'OpenReplay',
                  integrationData.openreplay,
                  'Project ID',
                  integrationData.openreplay?.projectId
                )}
                {renderIntegrationCard(
                  'snowplow',
                  'Snowplow',
                  integrationData.snowplow,
                  'Tracker ID',
                  integrationData.snowplow?.trackerId
                )}
              </div>
            </TabsContent>

            <TabsContent value="matomo" className="space-y-4">
              {renderIntegrationCard(
                'matomo',
                'Matomo Analytics',
                integrationData.matomo,
                'Site ID',
                integrationData.matomo?.siteId
              )}
              {integrationData.matomo?.exists && (
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => window.open(`https://analytics.pagemind.ai/index.php?module=CoreHome&action=index&idSite=${integrationData.matomo?.siteId}&period=day&date=today`, '_blank')}
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Open in Matomo Dashboard
                </Button>
              )}
            </TabsContent>

            <TabsContent value="openreplay" className="space-y-4">
              {renderIntegrationCard(
                'openreplay',
                'OpenReplay Session Recording',
                integrationData.openreplay,
                'Project ID',
                integrationData.openreplay?.projectId
              )}
              {integrationData.openreplay?.exists && integrationData.openreplay?.projectKey && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Project Key: <code className="ml-1">{integrationData.openreplay.projectKey}</code>
                  </AlertDescription>
                </Alert>
              )}
              {integrationData.openreplay?.exists && (
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => window.open(`https://app.openreplay.com/projects/${integrationData.openreplay?.projectId}/sessions`, '_blank')}
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Open in OpenReplay Dashboard
                </Button>
              )}
            </TabsContent>

            <TabsContent value="snowplow" className="space-y-4">
              {renderIntegrationCard(
                'snowplow',
                'Snowplow Analytics',
                integrationData.snowplow,
                'Tracker ID',
                integrationData.snowplow?.trackerId
              )}
              {integrationData.snowplow?.exists && integrationData.snowplow?.appId && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    App ID: <code className="ml-1">{integrationData.snowplow.appId}</code>
                  </AlertDescription>
                </Alert>
              )}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}